import type { ChatIceServer, ChatSignalType } from '@bits/shared/zod';

// Full mesh: one peer connection per other participant. The side with the
// lower person id makes the offer, so two peers never offer each other at
// once and no rollback logic is needed. Every connection carries an audio
// and a video line from the start (a trackless video transceiver on audio
// calls), so a screen can later be sent with replaceTrack and nothing ever
// renegotiates mid-call.

export interface RtpSenderLike {
  readonly track: MediaStreamTrack | null;
  replaceTrack(track: MediaStreamTrack | null): Promise<void>;
}

export interface RemoteTrackLike {
  readonly kind: string;
  readonly muted: boolean;
  onmute: (() => void) | null;
  onunmute: (() => void) | null;
}

export interface PeerConnectionLike {
  addTrack(track: MediaStreamTrack, stream: MediaStream): RtpSenderLike;
  addTransceiver(kind: 'audio' | 'video', init?: { direction: string }): { sender: RtpSenderLike };
  createOffer(): Promise<RTCSessionDescriptionInit>;
  createAnswer(): Promise<RTCSessionDescriptionInit>;
  setLocalDescription(description: RTCSessionDescriptionInit): Promise<void>;
  setRemoteDescription(description: RTCSessionDescriptionInit): Promise<void>;
  addIceCandidate(candidate: RTCIceCandidateInit): Promise<void>;
  close(): void;
  readonly localDescription: RTCSessionDescriptionInit | null;
  readonly remoteDescription: RTCSessionDescriptionInit | null;
  readonly connectionState: string;
  onicecandidate: ((event: { candidate: RTCIceCandidate | null }) => void) | null;
  ontrack:
    | ((event: { track: MediaStreamTrack & RemoteTrackLike; streams: readonly MediaStream[] }) => void)
    | null;
  onconnectionstatechange: (() => void) | null;
}

export interface MeshOptions {
  meId: string;
  localStream: MediaStream | null;
  send: (to: string, type: ChatSignalType, payload: unknown) => void;
  onRemoteStream: (peerId: string, stream: MediaStream | null) => void;
  onRemoteVideo?: (peerId: string, active: boolean) => void;
  onPeerScreen?: (peerId: string, sharing: boolean) => void;
  onPeerState?: (peerId: string, state: string) => void;
  createConnection?: () => PeerConnectionLike;
  iceServers?: ChatIceServer[];
}

interface Peer {
  pc: PeerConnectionLike;
  pendingIce: RTCIceCandidateInit[];
  videoSender: RtpSenderLike;
}

export interface StateSignal {
  screen: boolean;
}

export function isInitiator(meId: string, peerId: string): boolean {
  return meId < peerId;
}

export function parseStateSignal(payload: unknown): StateSignal | null {
  if (payload === null || typeof payload !== 'object') return null;
  const screen = (payload as { screen?: unknown }).screen;
  return typeof screen === 'boolean' ? { screen } : null;
}

export class CallMesh {
  private peers = new Map<string, Peer>();
  private closed = false;
  private screenTrack: MediaStreamTrack | null = null;
  private createConnection: () => PeerConnectionLike;

  constructor(private opts: MeshOptions) {
    this.createConnection =
      opts.createConnection ??
      (() =>
        new RTCPeerConnection({
          iceServers: (opts.iceServers ?? []).map((s) => ({
            urls: s.urls,
            ...(s.username !== undefined ? { username: s.username } : {}),
            ...(s.credential !== undefined ? { credential: s.credential } : {}),
          })),
        }) as unknown as PeerConnectionLike);
  }

  peerIds(): string[] {
    return [...this.peers.keys()];
  }

  isSharingScreen(): boolean {
    return this.screenTrack !== null;
  }

  async setPeers(ids: readonly string[]): Promise<void> {
    if (this.closed) return;
    const wanted = new Set(ids.filter((id) => id !== this.opts.meId));
    for (const id of [...this.peers.keys()]) {
      if (!wanted.has(id)) this.removePeer(id);
    }
    for (const id of wanted) {
      if (this.peers.has(id)) continue;
      const peer = this.addPeer(id);
      if (isInitiator(this.opts.meId, id)) await this.offer(id, peer);
      if (this.screenTrack) this.opts.send(id, 'state', { screen: true } satisfies StateSignal);
    }
  }

  async handleSignal(from: string, type: ChatSignalType, payload: unknown): Promise<void> {
    if (this.closed) return;
    const peer = this.peers.get(from) ?? this.addPeer(from);
    switch (type) {
      case 'offer': {
        await peer.pc.setRemoteDescription(payload as RTCSessionDescriptionInit);
        const answer = await peer.pc.createAnswer();
        await peer.pc.setLocalDescription(answer);
        this.opts.send(from, 'answer', peer.pc.localDescription ?? answer);
        await this.flushIce(peer);
        return;
      }
      case 'answer':
        await peer.pc.setRemoteDescription(payload as RTCSessionDescriptionInit);
        await this.flushIce(peer);
        return;
      case 'ice': {
        const candidate = payload as RTCIceCandidateInit;
        if (peer.pc.remoteDescription) await peer.pc.addIceCandidate(candidate);
        else peer.pendingIce.push(candidate);
        return;
      }
      case 'state': {
        const state = parseStateSignal(payload);
        if (state) this.opts.onPeerScreen?.(from, state.screen);
        return;
      }
    }
  }

  // The screen rides the video line of every peer: on a video call it stands
  // in for the camera, on an audio call it fills the empty transceiver. The
  // camera comes back when sharing stops.
  async setScreenTrack(track: MediaStreamTrack | null): Promise<void> {
    if (this.closed) return;
    this.screenTrack = track;
    const outgoing = track ?? this.cameraTrack();
    for (const [id, peer] of this.peers) {
      await peer.videoSender.replaceTrack(outgoing);
      this.opts.send(id, 'state', { screen: track !== null } satisfies StateSignal);
    }
  }

  close(): void {
    this.closed = true;
    this.screenTrack = null;
    for (const id of [...this.peers.keys()]) this.removePeer(id);
  }

  private cameraTrack(): MediaStreamTrack | null {
    return this.opts.localStream?.getVideoTracks()[0] ?? null;
  }

  private addPeer(id: string): Peer {
    const pc = this.createConnection();
    const local = this.opts.localStream;
    const audio = local?.getAudioTracks()[0];
    if (local && audio) pc.addTrack(audio, local);
    else pc.addTransceiver('audio', { direction: 'sendrecv' });
    const camera = this.cameraTrack();
    const videoSender =
      local && camera
        ? pc.addTrack(camera, local)
        : pc.addTransceiver('video', { direction: 'sendrecv' }).sender;
    const peer: Peer = { pc, pendingIce: [], videoSender };
    this.peers.set(id, peer);
    if (this.screenTrack) void videoSender.replaceTrack(this.screenTrack);
    pc.onicecandidate = (event) => {
      if (event.candidate) this.opts.send(id, 'ice', event.candidate.toJSON());
    };
    pc.ontrack = (event) => {
      const stream = event.streams[0] ?? new MediaStream([event.track]);
      this.opts.onRemoteStream(id, stream);
      if (event.track.kind === 'video') {
        // A video line that carries nothing yet (an audio call, a camera that
        // is off) stays muted; frames arriving is what unmutes it.
        const track = event.track;
        track.onunmute = () => this.opts.onRemoteVideo?.(id, true);
        track.onmute = () => this.opts.onRemoteVideo?.(id, false);
        this.opts.onRemoteVideo?.(id, !track.muted);
      }
    };
    pc.onconnectionstatechange = () => {
      this.opts.onPeerState?.(id, pc.connectionState);
    };
    return peer;
  }

  private async offer(id: string, peer: Peer): Promise<void> {
    const offer = await peer.pc.createOffer();
    await peer.pc.setLocalDescription(offer);
    this.opts.send(id, 'offer', peer.pc.localDescription ?? offer);
  }

  private async flushIce(peer: Peer): Promise<void> {
    const queued = peer.pendingIce;
    peer.pendingIce = [];
    for (const candidate of queued) await peer.pc.addIceCandidate(candidate);
  }

  private removePeer(id: string): void {
    const peer = this.peers.get(id);
    if (!peer) return;
    this.peers.delete(id);
    peer.pc.onicecandidate = null;
    peer.pc.ontrack = null;
    peer.pc.onconnectionstatechange = null;
    peer.pc.close();
    this.opts.onRemoteStream(id, null);
    this.opts.onRemoteVideo?.(id, false);
    this.opts.onPeerScreen?.(id, false);
  }
}
